import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Upload, Loader2, FileText, Copy, Check, XCircle } from "lucide-react";
import { toast, Toaster } from "sonner";
import AdminLayout from "../../components/AdminLayout";
import { api, formatApiErrorDetail } from "../../lib/api";
import { uploadFile } from "../../lib/storage";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminPitchDecks() {
  const [decks, setDecks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("decks");
  const [newDeck, setNewDeck] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [d, r] = await Promise.all([
        api.get("/admin/pitch-decks"),
        api.get("/admin/pitch-deck-requests"),
      ]);
      setDecks(d.data); setRequests(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file, "document");
      setNewDeck((cur) => ({ ...(cur || { title: file.name.replace(/\.pdf$/i, ""), description: "" }), storage_path: res.storage_path, filename: res.original_filename, size: res.size }));
      toast.success("Document uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setUploading(false); }
  };

  const saveDeck = async (e) => {
    e.preventDefault();
    if (!newDeck?.storage_path) { toast.error("Upload a PDF first"); return; }
    setBusy(true);
    try {
      await api.post("/admin/pitch-decks", {
        title: newDeck.title,
        description: newDeck.description || "",
        storage_path: newDeck.storage_path,
      });
      toast.success("Deck created");
      setNewDeck(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const removeDeck = async (id) => {
    if (!window.confirm("Delete this pitch deck?")) return;
    try { await api.delete(`/admin/pitch-decks/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/pitch-deck-requests/${id}/approve`);
      const fullUrl = `${BACKEND}${data.download_url}`;
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Approved. Download link copied to clipboard.");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const reject = async (id) => {
    try { await api.post(`/admin/pitch-deck-requests/${id}/reject`); toast.success("Rejected"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const copyLink = async (token) => {
    await navigator.clipboard.writeText(`${BACKEND}/api/pitch-deck/download?token=${token}`);
    toast.success("Link copied");
  };

  const pending = requests.filter((r) => r.status === "pending").length;
  const approved = requests.filter((r) => r.status === "approved").length;

  return (
    <AdminLayout title="Pitch Decks">
      <Toaster theme="dark" position="bottom-right" />
      <div className="flex flex-wrap gap-3 mb-6 justify-between items-end">
        <div className="flex gap-2">
          <button data-testid="admin-decks-tab-decks" onClick={() => setTab("decks")} className={`px-5 py-3 text-[12px] uppercase tracking-[0.22em] border ${tab === "decks" ? "bg-primary text-primary-foreground border-primary" : "border-primary/30 text-primary"}`}>
            Decks ({decks.length})
          </button>
          <button data-testid="admin-decks-tab-requests" onClick={() => setTab("requests")} className={`px-5 py-3 text-[12px] uppercase tracking-[0.22em] border ${tab === "requests" ? "bg-primary text-primary-foreground border-primary" : "border-primary/30 text-primary"}`}>
            Requests · {pending} pending · {approved} approved
          </button>
        </div>
        {tab === "decks" && (
          <button data-testid="admin-decks-new" onClick={() => setNewDeck({ title: "", description: "" })} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 text-[12px] uppercase tracking-[0.22em] hover:bg-primary/90">
            <Plus className="w-4 h-4" /> Upload New Deck
          </button>
        )}
      </div>

      {tab === "decks" && (
        <div className="glass overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-primary/20">
              <tr className="text-left">
                <th className="overline px-6 py-4">Title</th>
                <th className="overline px-6 py-4">File</th>
                <th className="overline px-6 py-4">Size</th>
                <th className="overline px-6 py-4">Created</th>
                <th className="overline px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {decks.length === 0 && <tr><td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">No pitch decks uploaded yet.</td></tr>}
              {decks.map((d) => (
                <tr key={d.id} data-testid={`admin-deck-row-${d.id}`} className="border-b border-primary/5">
                  <td className="px-6 py-4 font-serif text-lg">{d.title}</td>
                  <td className="px-6 py-4 font-mono text-xs text-foreground/60">{d.original_filename}</td>
                  <td className="px-6 py-4 font-mono text-xs">{(d.size || 0) < 1024 * 1024 ? `${Math.ceil((d.size || 0) / 1024)} KB` : `${((d.size || 0) / 1024 / 1024).toFixed(1)} MB`}</td>
                  <td className="px-6 py-4 font-mono text-xs text-foreground/60">{new Date(d.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => removeDeck(d.id)} className="p-2 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "requests" && (
        <div className="glass overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-deck-requests-table">
            <thead className="border-b border-primary/20">
              <tr className="text-left">
                <th className="overline px-6 py-4">Date</th>
                <th className="overline px-6 py-4">Name</th>
                <th className="overline px-6 py-4">Email</th>
                <th className="overline px-6 py-4">Type</th>
                <th className="overline px-6 py-4">Status</th>
                <th className="overline px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">No requests yet.</td></tr>}
              {requests.map((r) => (
                <tr key={r.id} data-testid={`admin-deck-request-row-${r.id}`} className="border-b border-primary/5">
                  <td className="px-6 py-4 font-mono text-xs text-foreground/60">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-6 py-4">{r.name}</td>
                  <td className="px-6 py-4 font-mono text-xs text-primary">{r.email}</td>
                  <td className="px-6 py-4 text-foreground/70">{r.investor_type} · {r.investment_range}</td>
                  <td className="px-6 py-4">
                    <span className={`overline ${r.status === "approved" ? "text-primary" : r.status === "rejected" ? "text-destructive" : "text-foreground/60"}`}>
                      {r.status} {r.auto_approved && "· auto"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right flex justify-end gap-2">
                    {r.status === "pending" && (
                      <>
                        <button data-testid={`admin-deck-approve-${r.id}`} onClick={() => approve(r.id)} className="p-2 text-primary hover:bg-primary hover:text-primary-foreground transition-colors" title="Approve & copy link"><Check className="w-4 h-4" /></button>
                        <button data-testid={`admin-deck-reject-${r.id}`} onClick={() => reject(r.id)} className="p-2 text-destructive" title="Reject"><XCircle className="w-4 h-4" /></button>
                      </>
                    )}
                    {r.status === "approved" && r.access_token && (
                      <button onClick={() => copyLink(r.access_token)} className="p-2 text-primary" title="Copy link"><Copy className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {newDeck && (
        <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-md overflow-y-auto p-6 lg:p-12">
          <div className="max-w-2xl mx-auto glass-strong p-8 lg:p-10">
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="overline mb-2">— Upload</div>
                <h2 className="font-serif text-3xl">New Pitch Deck</h2>
              </div>
              <button onClick={() => setNewDeck(null)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveDeck} data-testid="admin-decks-form" className="space-y-5">
              <div>
                <label className="overline mb-2 block">Title</label>
                <input data-testid="admin-decks-input-title" required value={newDeck.title} onChange={(e) => setNewDeck({ ...newDeck, title: e.target.value })} className="w-full bg-background border border-input px-4 py-3" />
              </div>
              <div>
                <label className="overline mb-2 block">Description (internal note)</label>
                <textarea rows={3} value={newDeck.description} onChange={(e) => setNewDeck({ ...newDeck, description: e.target.value })} className="w-full bg-background border border-input px-4 py-3 resize-none" />
              </div>
              <div>
                <label className="overline mb-2 block">PDF File</label>
                <div className="flex items-center gap-4">
                  {newDeck.storage_path ? (
                    <div className="flex items-center gap-2 text-primary"><FileText className="w-5 h-5" /><span className="font-mono text-xs">{newDeck.filename} · {(newDeck.size || 0) < 1024 * 1024 ? `${Math.ceil((newDeck.size || 0) / 1024)} KB` : `${((newDeck.size || 0) / 1024 / 1024).toFixed(1)} MB`}</span></div>
                  ) : (
                    <div className="text-muted-foreground text-sm">No file uploaded</div>
                  )}
                  <label className="ml-auto inline-flex items-center gap-2 border border-primary/40 text-primary px-4 py-3 text-[12px] uppercase tracking-[0.22em] hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? "Uploading…" : "Upload PDF"}
                    <input data-testid="admin-decks-file-input" type="file" accept="application/pdf" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-primary/10">
                <button type="button" onClick={() => setNewDeck(null)} className="px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-foreground/60">Cancel</button>
                <button data-testid="admin-decks-save" type="submit" disabled={busy} className="bg-primary text-primary-foreground px-6 py-3 text-[12px] uppercase tracking-[0.22em] disabled:opacity-60">
                  {busy ? "Saving…" : "Save Deck"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
